import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  useGolfApi,
  GolfOuting,
  Team,
} from '../services/golfApi';
import {
  Calendar,
  MapPin,
  Users,
  Edit2,
  Trash2,
  UserPlus,
  Shuffle,
  Save,
  ArrowLeft,
  Trophy,
  Play,
  CheckCircle,
  AlertTriangle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import ChatPanel from '../components/ChatPanel';

const GolfOutingDetailPage: React.FC = () => {
  const { outingId } = useParams<{ outingId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth0();
  const queryClient = useQueryClient();
  const golfApi = useGolfApi();
  const isNew = outingId === 'create';

  const [isEditing, setIsEditing] = useState(isNew);
  const [showTeamBuilder, setShowTeamBuilder] = useState(false);
  const [handicap, setHandicap] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  const { data: outing, isLoading: outingLoading } = useQuery({
    queryKey: ['golf', 'outing', outingId],
    queryFn: () => golfApi.getOuting(outingId!),
    enabled: !isNew,
  });

  const myParticipant = outing?.participants?.find((p) => p.userId === user?.sub);
  const hasJoined = !!myParticipant;

  useEffect(() => {
    if (myParticipant && typeof myParticipant.handicap === 'number') {
      setHandicap(myParticipant.handicap);
    }
  }, [myParticipant?.userId, myParticipant?.handicap]);

  const { data: courses = [] } = useQuery({
    queryKey: ['golf', 'courses'],
    queryFn: () => golfApi.getCourses(),
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<GolfOuting>) => golfApi.createOuting(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['golf', 'outings'] });
      toast.success('Outing created successfully');
      navigate(`/golf/outings/${data.outingId}`);
    },
    onError: () => {
      toast.error('Failed to create outing');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ outingId, updates }: { outingId: string; updates: Partial<GolfOuting> }) =>
      golfApi.updateOuting(outingId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['golf', 'outing', outingId] });
      queryClient.invalidateQueries({ queryKey: ['golf', 'outings'] });
      setIsEditing(false);
      toast.success('Outing updated successfully');
    },
    onError: () => {
      toast.error('Failed to update outing');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (outingId: string) => golfApi.deleteOuting(outingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['golf', 'outings'] });
      toast.success('Outing deleted successfully');
      navigate('/golf/outings');
    },
    onError: () => {
      toast.error('Failed to delete outing');
    },
  });

  const joinMutation = useMutation({
    mutationFn: ({ outingId, handicap }: { outingId: string; handicap: number }) =>
      golfApi.joinOuting(outingId, handicap),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['golf', 'outing', outingId] });
      toast.success(data?.message || (hasJoined ? 'Handicap updated' : 'Joined outing successfully'));
    },
    onError: (error: Error) => {
      toast.error(error?.message || (hasJoined ? 'Failed to update handicap' : 'Failed to join outing'));
    },
  });

  const generateTeamsMutation = useMutation({
    mutationFn: (outingId: string) => golfApi.generateTeams(outingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['golf', 'outing', outingId] });
      toast.success('Teams generated successfully');
    },
    onError: () => {
      toast.error('Failed to generate teams');
    },
  });

  const updateTeamsMutation = useMutation({
    mutationFn: ({ outingId, teams }: { outingId: string; teams: Team[] }) =>
      golfApi.updateTeams(outingId, teams),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['golf', 'outing', outingId] });
      setShowTeamBuilder(false);
      toast.success('Teams updated successfully');
    },
    onError: () => {
      toast.error('Failed to update teams');
    },
  });

  if (outingLoading && !isNew) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </div>
    );
  }

  // Show "not found" only if we're not creating a new outing and the outing doesn't exist
  if (!outing && !isNew) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Outing not found
          </h3>
        </div>
      </div>
    );
  }

  if (isEditing || isNew) {
    return (
      <OutingForm
        outing={outing}
        courses={courses}
        onSubmit={(data) => {
          if (isNew) {
            createMutation.mutate(data);
          } else {
            updateMutation.mutate({ outingId: outingId!, updates: data });
          }
        }}
        onCancel={() => {
          if (isNew) {
            navigate('/golf/outings');
          } else {
            setIsEditing(false);
          }
        }}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />
    );
  }

  return (
    <>
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/golf/outings')}
          className="mb-4 flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Outings
        </button>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
              {outing.name}
            </h1>
            <div className="flex items-center gap-4 text-gray-600 dark:text-gray-400">
              <span className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {format(new Date(outing.date), 'MMMM d, yyyy')}
              </span>
              <span className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                {outing.courseName}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                outing.status === 'upcoming' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                outing.status === 'in-progress' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
              }`}>
                {outing.status.replace('-', ' ')}
              </span>
            </div>
          </div>

          <div className="flex gap-2">
            {outing.status === 'upcoming' && (
              <button
                onClick={() => updateMutation.mutate({ outingId: outing.outingId, updates: { status: 'in-progress' } })}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <Play className="h-4 w-4" />
                Start Round
              </button>
            )}
            {outing.status === 'in-progress' && (
              <>
                <button
                  onClick={() => navigate(`/golf/outings/${outing.outingId}/scorecard`)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Scorecard
                </button>
                <button
                  onClick={() => updateMutation.mutate({ outingId: outing.outingId, updates: { status: 'completed' } })}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <CheckCircle className="h-4 w-4" />
                  Complete
                </button>
              </>
            )}
            {outing.status === 'completed' && (
              <button
                onClick={() => navigate(`/golf/outings/${outing.outingId}/leaderboard`)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Trophy className="h-4 w-4" />
                View Results
              </button>
            )}
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
            >
              <Edit2 className="h-4 w-4" />
              Edit
            </button>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="px-4 py-2 border border-red-300 dark:border-red-600 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          </div>
        </div>
        <div className="mt-4 lg:hidden">
          <Link
            to={`/chat/outing/${outing.outingId}`}
            state={{
              title: `${outing.name} chat`,
              subtitle: 'Outing participants only',
              locked: !outing.participants?.some((p) => p.userId === user?.sub),
              lockedMessage: 'Join this outing to chat with other players.',
            }}
            className="inline-flex items-center px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700"
          >
            Open Outing Chat
          </Link>
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Details Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Details</h2>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm text-gray-600 dark:text-gray-400">Format</dt>
                <dd className="text-base font-medium text-gray-900 dark:text-white capitalize">
                  {outing.format.replace('-', ' ')}
                </dd>
              </div>
              {outing.description && (
                <div>
                  <dt className="text-sm text-gray-600 dark:text-gray-400">Description</dt>
                  <dd className="text-base text-gray-900 dark:text-white">{outing.description}</dd>
                </div>
              )}
              <div>
                <dt className="text-sm text-gray-600 dark:text-gray-400">Number of Teams</dt>
                <dd className="text-base font-medium text-gray-900 dark:text-white">
                  {outing.teamsCount}
                </dd>
              </div>
            </dl>
          </div>

          {/* Teams */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Teams</h2>
              <div className="flex gap-2">
                {outing.teams && outing.teams.length > 0 && (
                  <button
                    onClick={() => setShowTeamBuilder(true)}
                    className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center gap-2"
                  >
                    <Edit2 className="h-3 w-3" />
                    Edit
                  </button>
                )}
                <button
                  onClick={() => generateTeamsMutation.mutate(outing.outingId)}
                  disabled={generateTeamsMutation.isPending || !outing.participants || outing.participants.length === 0}
                  className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Shuffle className="h-3 w-3" />
                  {outing.teams && outing.teams.length > 0 ? 'Regenerate' : 'Generate'} Teams
                </button>
              </div>
            </div>

            {outing.teams && outing.teams.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {outing.teams.map((team) => (
                  <div key={team.teamId} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                      {team.teamName}
                    </h3>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                      Avg Handicap: {team.averageHandicap.toFixed(1)}
                    </div>
                    <div className="space-y-2">
                      {team.players.map((player, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span className="text-gray-900 dark:text-white">{player.email}</span>
                          <span className="text-gray-600 dark:text-gray-400">HCP: {player.handicap}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-600 dark:text-gray-400">
                No teams generated yet. Add participants and generate teams.
              </div>
            )}
          </div>

          {/* Team Builder Modal */}
          {showTeamBuilder && outing.teams && (
            <TeamBuilderModal
              teams={outing.teams}
              onSave={(teams) => updateTeamsMutation.mutate({ outingId: outing.outingId, teams })}
              onClose={() => setShowTeamBuilder(false)}
              isSubmitting={updateTeamsMutation.isPending}
            />
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Join Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Users className="h-5 w-5" />
              Participants ({outing.participants?.length || 0})
            </h2>

            <div className="space-y-3 mb-4">
              {outing.participants && outing.participants.length > 0 ? (
                outing.participants.map((participant, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                    <span className="text-sm text-gray-900 dark:text-white">{participant.email}</span>
                    <span className="text-xs text-gray-600 dark:text-gray-400">HCP: {participant.handicap}</span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-600 dark:text-gray-400 text-center py-4">
                  No participants yet
                </p>
              )}
            </div>

            {outing.status === 'upcoming' && (
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Your Handicap
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="54"
                    step="1"
                    value={handicap}
                    onChange={(e) => {
                      const next = parseInt(e.target.value, 10)
                      setHandicap(Number.isFinite(next) ? next : 0)
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0"
                  />
                </div>
                <button
                  onClick={() => joinMutation.mutate({ outingId: outing.outingId, handicap })}
                  disabled={joinMutation.isPending}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {hasJoined ? <Save className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
                  {joinMutation.isPending
                    ? (hasJoined ? 'Saving...' : 'Joining...')
                    : (hasJoined ? 'Update Handicap' : 'Join Outing')}
                </button>
              </div>
            )}
          </div>

          {/* Side Games */}
          {outing.sideGames && outing.sideGames.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Trophy className="h-5 w-5" />
                Side Games
              </h2>
              <div className="space-y-2">
                {outing.sideGames.map((game, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 text-sm">
                    <span className="text-gray-900 dark:text-white capitalize">
                      {game.type.replace('-', ' ')}
                    </span>
                    <span className="text-gray-600 dark:text-gray-400">Hole {game.hole}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="hidden lg:block">
            <ChatPanel
              kind="outing"
              entityId={outing.outingId}
              title={`${outing.name} chat`}
              subtitle="Outing participants only"
              placeholder="Message this outing…"
              compact
              className="sticky top-24"
              locked={!outing.participants?.some((p) => p.userId === user?.sub)}
              lockedMessage="Join this outing to chat with other players."
            />
          </div>
        </div>
      </div>
    </div>

    {/* Delete Confirmation Modal */}
    {showDeleteModal && (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
          <div className="mt-3 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900">
              <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mt-4">
              Delete Outing
            </h3>
            <div className="mt-2 px-7 py-3">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                This action cannot be undone. This will permanently delete the outing "{outing?.name}", 
                all teams, scores, and participant data.
              </p>
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Type "<span className="font-mono bg-gray-100 dark:bg-gray-700 px-1 rounded">DELETE</span>" to confirm:
                </p>
                <input
                  type="text"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && deleteConfirmation === 'DELETE' && outing) {
                      deleteMutation.mutate(outing.outingId);
                      setShowDeleteModal(false);
                      setDeleteConfirmation('');
                    }
                  }}
                  placeholder="Type DELETE here"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  autoFocus
                />
              </div>
            </div>
            <div className="items-center px-4 py-3">
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmation('');
                  }}
                  className="flex-1 px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md shadow-sm hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (deleteConfirmation === 'DELETE' && outing) {
                      deleteMutation.mutate(outing.outingId);
                      setShowDeleteModal(false);
                      setDeleteConfirmation('');
                    }
                  }}
                  disabled={deleteConfirmation !== 'DELETE' || deleteMutation.isPending}
                  className="flex-1 px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

// Outing Form Component
interface OutingFormProps {
  outing?: GolfOuting;
  courses: any[];
  onSubmit: (data: Partial<GolfOuting>) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

const OutingForm: React.FC<OutingFormProps> = ({ outing, courses, onSubmit, onCancel, isSubmitting }) => {
  const [formData, setFormData] = useState({
    name: outing?.name || '',
    courseId: outing?.courseId || '',
    date: outing?.date ? format(new Date(outing.date), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
    format: outing?.format || 'scramble',
    teamsCount: outing?.teamsCount || 4,
    description: outing?.description || '',
    sideGames: outing?.sideGames || [],
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Convert date string to ISO format at noon UTC to avoid timezone issues
    const dateAtNoon = new Date(formData.date + 'T12:00:00Z').toISOString();
    onSubmit({
      ...formData,
      date: dateAtNoon,
    });
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
          {outing ? 'Edit Outing' : 'Create New Outing'}
        </h1>

        <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Outing Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Spring Scramble 2025"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Golf Course *
            </label>
            <select
              value={formData.courseId}
              onChange={(e) => setFormData({ ...formData, courseId: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">Select a course</option>
              {courses.map((course) => (
                <option key={course.courseId} value={course.courseId}>
                  {course.name} - {course.location}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Date *
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Format *
              </label>
              <select
                value={formData.format}
                onChange={(e) => setFormData({ ...formData, format: e.target.value as any })}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="scramble">Scramble</option>
                <option value="best-ball">Best Ball</option>
                <option value="stroke-play">Stroke Play</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Teams *
              </label>
              <input
                type="number"
                min="2"
                max="10"
                value={formData.teamsCount}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  setFormData({ ...formData, teamsCount: isNaN(value) ? 4 : value });
                }}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Optional description for the outing..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              {isSubmitting ? 'Saving...' : outing ? 'Update Outing' : 'Create Outing'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Team Builder Modal
interface TeamBuilderModalProps {
  teams: Team[];
  onSave: (teams: Team[]) => void;
  onClose: () => void;
  isSubmitting: boolean;
}

const TeamBuilderModal: React.FC<TeamBuilderModalProps> = ({ teams: initialTeams, onSave, onClose, isSubmitting }) => {
  const [teams, setTeams] = useState(initialTeams);

  // Drag and drop would be implemented here for better UX
  // For now, simple team name editing

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Edit Teams</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {teams.map((team, idx) => (
            <div key={team.teamId} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <input
                type="text"
                value={team.teamName}
                onChange={(e) => {
                  const newTeams = [...teams];
                  newTeams[idx].teamName = e.target.value;
                  setTeams(newTeams);
                }}
                className="text-lg font-semibold mb-3 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <div className="space-y-2">
                {team.players.map((player, pIdx) => (
                  <div key={pIdx} className="flex items-center justify-between text-sm">
                    <span className="text-gray-900 dark:text-white">{player.email}</span>
                    <span className="text-gray-600 dark:text-gray-400">HCP: {player.handicap}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(teams)}
            disabled={isSubmitting}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
          >
            <Save className="h-4 w-4" />
            {isSubmitting ? 'Saving...' : 'Save Teams'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GolfOutingDetailPage;

